import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import UnderConstructionBanner from "@/components/UnderConstructionBanner";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Signup from "@/pages/Signup";
import LeaveReview from "@/pages/LeaveReview";
import AdminTestimonials from "@/pages/AdminTestimonials";
import AdminBanner from "@/pages/AdminBanner";
import FAQ from "@/pages/FAQ";
import Properties from "@/pages/Properties";
import PortfolioLogin from "@/pages/PortfolioLogin";
import PortfolioDashboard from "@/pages/PortfolioDashboard";
import AddProperty from "@/pages/AddProperty";
import EditProperty from "@/pages/EditProperty";
import PropertyDetail from "@/pages/PropertyDetail";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";
import SelectRole from "@/pages/SelectRole";
import ChatAgent from "@/pages/ChatAgent";
import GoogleOAuthHandler from "@/components/GoogleOAuthHandler";
import { Toaster } from "@/components/ui/sonner";
import { PortfolioAuthProvider } from "@/context/PortfolioAuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Page View Tracker Component
function PageViewTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Don't track admin routes
    if (location.pathname.startsWith('/admin')) {
      return;
    }
    
    // Track page view
    const trackPageView = async () => {
      try {
        await axios.post(`${API}/analytics/track`, {
          page_url: location.pathname,
          referrer: document.referrer || ''
        });
      } catch (error) {
        // Silently fail - don't affect user experience
        console.debug('Analytics tracking failed:', error);
      }
    };
    
    trackPageView();
  }, [location.pathname]);
  
  return null;
}

function App() {
  const [bannerSettings, setBannerSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBannerSettings = async () => {
      try {
        const response = await axios.get(`${API}/banner-settings`);
        setBannerSettings(response.data);
      } catch (error) {
        console.error('Error fetching banner settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBannerSettings();
  }, []);

  const showBanner = bannerSettings?.is_active && !loading;

  return (
    <div className="App">
      <BrowserRouter>
        <PortfolioAuthProvider>
          <PageViewTracker />
          <GoogleOAuthHandler />
          {showBanner && <UnderConstructionBanner message={bannerSettings.message} />}
          <div style={{ marginTop: showBanner ? '48px' : '0' }}>
            <Navigation />
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/properties" element={<Properties />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/leave-review" element={<LeaveReview />} />
            <Route path="/admin/testimonials" element={<AdminTestimonials />} />
            <Route path="/admin/banner" element={<AdminBanner />} />
            
            {/* Portfolio Routes */}
            <Route path="/portfolio/login" element={<PortfolioLogin />} />
            <Route path="/portfolio/select-role" element={<SelectRole />} />
            <Route path="/portfolio/auth/callback" element={<GoogleAuthCallback />} />
            <Route path="/portfolio" element={<PortfolioDashboard />} />
            <Route path="/portfolio/add-property" element={<AddProperty />} />
            <Route path="/portfolio/edit-property/:id" element={<EditProperty />} />
            <Route path="/portfolio/property/:id" element={<PropertyDetail />} />
            
            {/* Chat Agent Route */}
            <Route path="/chat-agent" element={<ChatAgent />} />
          </Routes>
          <Footer />
        </div>
        <Toaster position="top-center" />
        </PortfolioAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
